import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { DialogActions } from 'src/app/model/dictionary';
import { PacoDialogAction } from 'src/app/components/dialogs/paco-dialog/paco-dialog.component';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { ReopenRequestResultDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/reopen-request-result-dto';
import { DataExchangeSubRequestPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';

export interface ReopenSubRequestsDialogData {
  requestId: number;
  requestName: string | null;
}

export interface ReopenSubRequestsDialogResult {
  event: DialogActions;
  results?: ReopenRequestResultDto[];
}

@Component({
  selector: 'app-reopen-sub-requests-dialog',
  templateUrl: './reopen-sub-requests-dialog.component.html',
  styleUrls: ['./reopen-sub-requests-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReopenSubRequestsDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ReopenSubRequestsDialogComponent>);
  private readonly data = inject<ReopenSubRequestsDialogData>(MAT_DIALOG_DATA);
  private readonly dataExchangeRequestApiService = inject(DataExchangeRequestApiService);

  readonly displayedColumns = ['select', 'id', 'name', 'articleNumber', 'status'];
  readonly pageSizeOptions = [5, 10, 25];
  readonly dataSource = new PaginationDataSource<DataExchangeSubRequestDto>(query => this.dataExchangeRequestApiService.getSubRequestsByPage(query, true));

  readonly commentControl = new FormControl('', { nonNullable: true });
  readonly selectedIds = signal<ReadonlySet<number>>(new Set());
  readonly allIds = signal<number[]>([]);
  readonly totalCount = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizeOptions[1]);
  readonly isSaving = signal(false);
  readonly errorMessageKey = signal<string | null>(null);

  readonly allSelected = computed(() => this.allIds().length > 0 && this.selectedIds().size === this.allIds().length);
  readonly someSelected = computed(() => this.selectedIds().size > 0 && !this.allSelected());

  readonly dialogActions = computed<PacoDialogAction[]>(() => [
    { name: DialogActions.REJECT, labelKey: 'common.buttons.cancel', disabled: this.isSaving() },
    { name: DialogActions.CONFIRM, labelKey: 'common.buttons.confirm', color: 'primary', disabled: this.selectedIds().size === 0 || this.isSaving() },
  ]);

  constructor() {
    this.dataSource.page.pipe(takeUntilDestroyed()).subscribe(page => {
      this.allIds.set(page.allIds);
      this.totalCount.set(page.totalItemCount);
      this.pageIndex.set(page.pageIndex);
      this.selectedIds.update(selected => new Set([...selected].filter(id => page.allIds.includes(id))));
    });

    this.dataSource.fetchData(
      0,
      this.pageSize(),
      [
        { fieldName: DataExchangeSubRequestPaginationFields.DataExchangeRequestId, values: [this.data.requestId.toString()] },
        {
          fieldName: DataExchangeSubRequestPaginationFields.Status,
          values: [DataExchangeRequestStatusDto.Closed, DataExchangeRequestStatusDto.Submitted].map(status => status.toString()),
        },
        { fieldName: DataExchangeSubRequestPaginationFields.HasPendingReopen, values: ['false'] },
      ],
      DataExchangeSubRequestPaginationFields.Id,
      true
    );
  }

  get requestName(): string | null {
    return this.data.requestName;
  }

  getName(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.articleName ?? sub.multiMaterial?.articleName ?? sub.packagingUnitInfo?.productName ?? sub.packagingSystemInfo?.productName ?? '';
  }

  getArticleNumber(sub: DataExchangeSubRequestDto): string {
    return sub.packagingComponent?.articleNumber ?? sub.multiMaterial?.articleNumber ?? sub.packagingUnitInfo?.articleNumber ?? sub.packagingSystemInfo?.articleNumber ?? '';
  }

  getStatusLabel(status: DataExchangeRequestStatusDto): string {
    return `dataManagement.dataExchangeRequests.status.${DataExchangeRequestStatusDto[status]}`;
  }

  isSelected(id: number): boolean {
    return this.selectedIds().has(id);
  }

  toggle(id: number, checked: boolean): void {
    this.selectedIds.update(selected => {
      const next = new Set(selected);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  toggleAll(checked: boolean): void {
    this.selectedIds.set(checked ? new Set(this.allIds()) : new Set());
  }

  onPageChanged(event: PageEvent): void {
    this.pageSize.set(event.pageSize);
    this.dataSource.fetchData(event.pageIndex, event.pageSize);
  }

  onDialogAction(action: DialogActions): void {
    if (action === DialogActions.CONFIRM) {
      this.confirm();
    } else {
      this.cancel();
    }
  }

  confirm(): void {
    const subRequestIds = [...this.selectedIds()];
    if (subRequestIds.length === 0 || this.isSaving()) {
      return;
    }
    const comment = this.commentControl.value.trim();
    this.errorMessageKey.set(null);
    this.isSaving.set(true);
    this.dataExchangeRequestApiService.requestReopen(this.data.requestId, { subRequestIds, comment: comment || null }, true).subscribe({
      next: results => {
        this.isSaving.set(false);
        this.dialogRef.close({ event: DialogActions.CONFIRM, results } satisfies ReopenSubRequestsDialogResult);
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving.set(false);
        if (error.status === 409) {
          this.errorMessageKey.set('dataManagement.dataExchangeRequests.reopenDialog.conflictError');
          this.dataSource.refresh();
        } else {
          this.errorMessageKey.set('dataManagement.dataExchangeRequests.reopenDialog.genericError');
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close({ event: DialogActions.REJECT } satisfies ReopenSubRequestsDialogResult);
  }
}
